/**
 * Single source of truth for Artistree colours and the fluid heading scale. The MUI theme reads the
 * TS values; SCSS reads the same values through the `--at-*` custom properties that
 * `injectArtistreeCssVariables` writes to <head> when this module is first evaluated (which happens
 * before the first render, so there is no flash of unthemed content).
 *
 * Brand colours are the same in both themes; the semantic set flips with `[data-theme="dark"]`.
 */

export const artistreeBrandColors = {
  primaryGreen: "hsl(128, 52%, 47%)",
  secondaryGreen: "hsl(86,53%,51%)",
  red: "hsl(335, 82%, 54%)",
  purple: "hsl(279, 87%, 69%)",
  yellow: "hsl(45, 100%, 60%)",
  orange: "hsl(35, 93%, 58%)",
  black: "hsl(345, 6%, 13%)",
  primaryBlue: "hsl(198, 74%, 51%)",
  secondaryBlue: "hsl(220, 83%, 50%)",
  primaryBlueLight: "hsl(179, 98%, 35%)",
  secondaryBlueLight: "hsl(185, 90%, 62%)",
  grey: "hsl(0, 0%, 42%)",
  white: "hsl(0,0%,100%)",
  offBrandDarkGreen: "hsl(181, 35%, 27%)",
} as const;

export const artistreeDarkConstants = {
  mainBackground: "#010409",
  blackSecondary: "#0d1117",
  blueGrey: "#161b22",
  iceBlue: "#c9d1d9",
  greyBorder: "hsl(213,10%,18%)",
} as const;

export type ArtistreeSemanticColors = {
  /** what the page itself paints behind everything */
  background: string,
  /** MUI `palette.background.default` only; nothing paints it without CssBaseline */
  backgroundDefault: string,
  /** cards, paper, modals, inputs (MUI `palette.background.paper`) */
  surface: string,
  /** app bar, popovers, outlined chips: the slightly raised surface */
  surfaceSecondary: string,
  text: string,
  textSecondary: string,
  border: string,
};

export const artistreeLightColors: ArtistreeSemanticColors = {
  background: artistreeBrandColors.white,
  backgroundDefault: "#fafafa",
  surface: artistreeBrandColors.white,
  surfaceSecondary: artistreeBrandColors.white,
  text: artistreeBrandColors.black,
  textSecondary: artistreeBrandColors.grey,
  border: "#d3d3d3",
};

export const artistreeDarkColors: ArtistreeSemanticColors = {
  background: artistreeDarkConstants.mainBackground,
  backgroundDefault: artistreeDarkConstants.blackSecondary,
  surface: artistreeDarkConstants.blackSecondary,
  surfaceSecondary: artistreeDarkConstants.blueGrey,
  text: artistreeDarkConstants.iceBlue,
  textSecondary: "#8b949e",
  border: artistreeDarkConstants.greyBorder,
};

/**
 * Fluid heading sizes. Each resolves to the shared MUI/Styling.scss size at viewports of 1200px and
 * up, and shrinks linearly down to the small-screen size at 360px.
 */
export const artistreeHeadingFontSizes = {
  h1: "clamp(40px, 17.23px + 6.325vw, 92.5px)",
  h2: "clamp(32px, 20.81px + 3.108vw, 57.8px)",
  h3: "clamp(28px, 20.11px + 2.193vw, 46.2px)",
  h4: "clamp(24px, 20.23px + 1.048vw, 32.7px)",
  h5: "clamp(20px, 18.66px + 0.373vw, 23.1px)",
  h6: "clamp(18px, 17.48px + 0.145vw, 19.2px)",
} as const;

const brandVariables: Record<string, string> = {
  "--at-clr-primary-green": artistreeBrandColors.primaryGreen,
  "--at-clr-secondary-green": artistreeBrandColors.secondaryGreen,
  "--at-clr-red": artistreeBrandColors.red,
  "--at-clr-purple": artistreeBrandColors.purple,
  "--at-clr-yellow": artistreeBrandColors.yellow,
  "--at-clr-orange": artistreeBrandColors.orange,
  "--at-clr-black": artistreeBrandColors.black,
  "--at-clr-primary-blue": artistreeBrandColors.primaryBlue,
  "--at-clr-secondary-blue": artistreeBrandColors.secondaryBlue,
  "--at-clr-primary-blue-light": artistreeBrandColors.primaryBlueLight,
  "--at-clr-secondary-blue-light": artistreeBrandColors.secondaryBlueLight,
  "--at-clr-grey": artistreeBrandColors.grey,
  "--at-clr-white": artistreeBrandColors.white,
  "--at-clr-off-brand-dark-green": artistreeBrandColors.offBrandDarkGreen,
  "--at-clr-black-main-background": artistreeDarkConstants.mainBackground,
  "--at-clr-black-secondary": artistreeDarkConstants.blackSecondary,
  "--at-clr-blue-grey": artistreeDarkConstants.blueGrey,
  "--at-clr-ice-blue": artistreeDarkConstants.iceBlue,
  "--at-clr-grey-border": artistreeDarkConstants.greyBorder,
  "--at-font-size-h1": artistreeHeadingFontSizes.h1,
  "--at-font-size-h2": artistreeHeadingFontSizes.h2,
  "--at-font-size-h3": artistreeHeadingFontSizes.h3,
  "--at-font-size-h4": artistreeHeadingFontSizes.h4,
  "--at-font-size-h5": artistreeHeadingFontSizes.h5,
  "--at-font-size-h6": artistreeHeadingFontSizes.h6,
};

function semanticVariables(colors: ArtistreeSemanticColors): Record<string, string> {
  return {
    "--at-background": colors.background,
    "--at-surface": colors.surface,
    "--at-surface-secondary": colors.surfaceSecondary,
    "--at-text": colors.text,
    "--at-text-secondary": colors.textSecondary,
    "--at-border": colors.border,
  };
}

function toDeclarations(variables: Record<string, string>): string {
  return Object.entries(variables).map(([name, value]) => `${name}:${value};`).join("");
}

export function getArtistreeCssVariables(): string {
  return `:root{${toDeclarations({...brandVariables, ...semanticVariables(artistreeLightColors)})}}` +
    `[data-theme="dark"]{${toDeclarations(semanticVariables(artistreeDarkColors))}}`;
}

export const ARTISTREE_TOKENS_STYLE_ID = "artistree-tokens";

export function injectArtistreeCssVariables(): void {
  if (typeof document === "undefined" || document.getElementById(ARTISTREE_TOKENS_STYLE_ID)) {
    return;
  }
  const style = document.createElement("style");
  style.id = ARTISTREE_TOKENS_STYLE_ID;
  style.textContent = getArtistreeCssVariables();
  document.head.appendChild(style);
}

injectArtistreeCssVariables();
